import { useState, useEffect, useRef } from 'react';
  const perms = detectPerms();
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Trash2, 
  X, 
  PackagePlus, 
  Search,
  AlertCircle,
  Loader2,
  Check,
  ChevronDown,
  Eye
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  product_id: '',
  quantity: '',
  tax_id: '',
  purchase_rate: '',
  sale_rate: '',
  display_rate: '',
  vendor_id: '',
  firm_id: '',
  invoice_date: '',
  invoice_number: '',
  payment_term_id: '',
  generate_items: true
};

export const Inward = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const dropdownRef = useRef(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [entriesRes, productsRes, taxesRes, vendorsRes, companiesRes, ptRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/inward`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/vendors`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/payment-terms`, getAuthHeaders())
      ]);
      setEntries(entriesRes.data);
      setProducts(productsRes.data);
      setTaxes(taxesRes.data);
      setVendors(vendorsRes.data);
      setCompanies(companiesRes.data);
      setPaymentTerms(ptRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setFormData(prev => ({ ...prev, product_id: product.id }));
    setProductSearch(product.name);
    setShowProductDropdown(false);
  };

  const filteredProducts = products.filter(p =>
    p.active && (
      p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.product_code?.toLowerCase().includes(productSearch.toLowerCase())
    )
  );

  const openAddModal = () => {
    setFormData(initialFormData);
    setSelectedProduct(null);
    setProductSearch('');
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.product_id) {
      setError('Please select a product');
      return;
    }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      setError('Please enter a valid quantity');
      return;
    }
    if (!formData.purchase_rate || parseFloat(formData.purchase_rate) <= 0) {
      setError('Please enter a valid purchase rate');
      return;
    }
    if (!formData.sale_rate || parseFloat(formData.sale_rate) <= 0) {
      setError('Please enter a valid sale rate');
      return;
    }
    if (!formData.display_rate || parseFloat(formData.display_rate) <= 0) {
      setError('Please enter a valid display rate');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        product_id: formData.product_id,
        quantity: parseFloat(formData.quantity),
        tax_id: formData.tax_id || null,
        purchase_rate: parseFloat(formData.purchase_rate),
        sale_rate: parseFloat(formData.sale_rate),
        display_rate: parseFloat(formData.display_rate),
        vendor_id: formData.vendor_id || null,
        firm_id: formData.firm_id || null,
        invoice_date: formData.invoice_date || null,
        invoice_number: formData.invoice_number || null,
        payment_term_id: formData.payment_term_id || null,
        generate_items: formData.generate_items
      };

      await axios.post(`${BACKEND_URL}/api/inward`, payload, getAuthHeaders());
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/inward/${entryId}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="inward-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Inward</h1>
          <p className="text-slate-600 mt-1">Add stock entries for products</p>
        </div>
        {perms.canAdd && (
        <button
          onClick={openAddModal}
          data-testid="add-inward-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Inward Entry
        </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Entries Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <PackagePlus className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No inward entries yet</h3>
            <p className="text-slate-500 mb-6">Start by adding your first stock entry</p>
            <button
              onClick={openAddModal}
              data-testid="empty-add-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add First Entry
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Batch</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Product</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Company</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Vendor</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Qty</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Items</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Batch Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Actions</div>
            </div>

            {/* Table Rows */}
            {entries.map((entry) => (
              <div
                key={entry.id}
                data-testid={`inward-row-${entry.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div className="text-slate-600 text-sm">{formatDate(entry.created_at)}</div>
                <div className="col-span-2">
                  <div className="font-mono text-sm font-medium text-slate-900">{entry.batch_number || '-'}</div>
                </div>
                <div className="col-span-2">
                  <div className="font-medium text-slate-900">{entry.product_name}</div>
                  {entry.product_code && (
                    <div className="text-xs text-slate-500 font-mono">{entry.product_code}</div>
                  )}
                </div>
                <div className="text-slate-600 text-sm">{entry.firm_name || '-'}</div>
                <div className="text-slate-600 text-sm">{entry.vendor_name || '-'}</div>
                <div className="font-medium text-slate-900">{entry.quantity}</div>
                <div className="text-sm">
                  {entry.items_count > 0 ? (
                    <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-medium">{entry.items_count}</span>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </div>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase ${
                    entry.batch_status === 'Complete' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`} data-testid={`batch-status-${entry.id}`}>
                    {entry.batch_status || 'Pending'}
                  </span>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                  <button
                    onClick={() => navigate(`/admin/inward/${entry.id}`)}
                    data-testid={`view-btn-${entry.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(entry.id)}
                    data-testid={`delete-btn-${entry.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this entry? This will reduce the inventory quantity.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                Add Inward Entry
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Product Search - Full Width */}
              <div ref={dropdownRef} className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Select Product <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setShowProductDropdown(true);
                        if (!e.target.value) {
                          setSelectedProduct(null);
                          setFormData(prev => ({ ...prev, product_id: '' }));
                        }
                      }}
                      onFocus={() => setShowProductDropdown(true)}
                      placeholder="Search product by name or code..."
                      data-testid="input-product-search"
                      className="w-full pl-10 pr-10 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                  
                  {showProductDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-60 overflow-y-auto">
                      {filteredProducts.length === 0 ? (
                        <div className="px-4 py-3 text-slate-400 text-sm">No products found</div>
                      ) : (
                        filteredProducts.map(product => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => handleProductSelect(product)}
                            className={`w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between ${
                              selectedProduct?.id === product.id ? 'bg-orange-50' : ''
                            }`}
                          >
                            <div>
                              <div className="font-medium text-slate-900">{product.name}</div>
                              {product.product_code && (
                                <div className="text-xs text-slate-500 font-mono">{product.product_code}</div>
                              )}
                            </div>
                            {selectedProduct?.id === product.id && (
                              <Check className="h-4 w-4 text-orange-500" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 2-Column Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    data-testid="input-quantity"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Tax */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tax</label>
                  <select
                    name="tax_id"
                    value={formData.tax_id}
                    onChange={handleInputChange}
                    data-testid="input-tax"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Select Tax</option>
                    {taxes.filter(t => t.active).map(tax => (
                      <option key={tax.id} value={tax.id}>{tax.name} ({tax.percentage}%)</option>
                    ))}
                  </select>
                </div>

                {/* Vendor */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Select Vendor</label>
                  <select
                    name="vendor_id"
                    value={formData.vendor_id}
                    onChange={handleInputChange}
                    data-testid="input-vendor"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Select Vendor</option>
                    {vendors.filter(v => v.active).map(vendor => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                </div>

                {/* Inward Firm (Company) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Select Inward Firm</label>
                  <select
                    name="firm_id"
                    value={formData.firm_id}
                    onChange={handleInputChange}
                    data-testid="input-firm"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Select Firm</option>
                    {companies.filter(c => c.active).map(company => (
                      <option key={company.id} value={company.id}>{company.company_name}</option>
                    ))}
                  </select>
                </div>

                {/* Invoice Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    name="invoice_date"
                    value={formData.invoice_date}
                    onChange={handleInputChange}
                    data-testid="input-invoice-date"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Invoice Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleInputChange}
                    placeholder="Enter invoice number"
                    data-testid="input-invoice-number"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Payment Terms */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Terms</label>
                  <select
                    name="payment_term_id"
                    value={formData.payment_term_id}
                    onChange={handleInputChange}
                    data-testid="input-payment-term"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Select Payment Term</option>
                    {paymentTerms.filter(pt => pt.active).map(pt => (
                      <option key={pt.id} value={pt.id}>{pt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Purchase Rate */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Purchase Rate (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="purchase_rate"
                    value={formData.purchase_rate}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    data-testid="input-purchase-rate"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Sale Rate */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Sale Rate (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="sale_rate"
                    value={formData.sale_rate}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    data-testid="input-sale-rate"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Display Rate */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Display Rate (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="display_rate"
                    value={formData.display_rate}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    data-testid="input-display-rate"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Generate Individual Items Toggle - Full Width */}
              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-sm mt-4">
                <div>
                  <div className="text-sm font-medium text-slate-700">Generate Individual Items</div>
                  <div className="text-xs text-slate-500 mt-0.5">Creates a unique Item ID for each unit</div>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, generate_items: !prev.generate_items }))}
                  data-testid="toggle-generate-items"
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    formData.generate_items ? 'bg-orange-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    formData.generate_items ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Add Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
